import { useContext } from 'react';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { LanguageContext } from '../App';
import { usePwaInstall } from '../utils/pwaInstall';
import { isKayiComNativeApp } from '../utils/androidApp';
import { Button } from '@/components/ui/button';

export const HERO_BUTTON_CLASS = 'rounded-md px-3 h-8 text-xs font-semibold';

/*
 * "Install App" installs the storefront as a Chrome PWA (web app). We always
 * try the native install prompt first; if the browser has not offered it yet we
 * show a short add-to-home-screen hint (iOS / in-app browsers).
 */
const InstallAppButton = ({ variant = 'navlink', onClick }) => {
  const { t } = useContext(LanguageContext);
  const { installed, install } = usePwaInstall();

  if (isKayiComNativeApp() || installed) return null;

  const label = t('installApp');

  const handleClick = async (e) => {
    if (e) e.preventDefault();
    if (onClick) onClick();

    const outcome = await install();
    if (outcome === 'accepted') {
      toast.success(t('appInstalledToast'));
      return;
    }
    if (outcome === 'dismissed') return;
    toast.info(t('installAppHint'));
  };

  if (variant === 'menu') {
    return (
      <button
        type="button"
        onClick={handleClick}
        data-testid="menu-install-app"
        className="w-full flex items-center justify-between px-3 py-3 rounded-xl text-green-400 hover:bg-white/5 transition"
      >
        <span className="flex items-center gap-3 min-w-0">
          <span className="text-base flex-shrink-0">📲</span>
          <span className="text-sm font-medium truncate">{label}</span>
        </span>
      </button>
    );
  }

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={handleClick}
        data-testid="nav-install-app-icon"
        aria-label={label}
        title={label}
        className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-lg text-green-400 hover:bg-green-500/10 hover:text-green-300 transition"
      >
        <Download size={18} />
      </button>
    );
  }

  if (variant === 'hero') {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={handleClick}
        data-testid="install-app-btn"
        className={`${HERO_BUTTON_CLASS} border-green-500/40 text-green-400 hover:bg-green-500/10 hover:text-green-300`}
      >
        <Download size={14} />
        {label}
      </Button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="nav-install-app"
      className="text-green-400 font-medium hover:text-green-300 transition inline-flex items-center gap-1.5"
    >
      <Download size={14} />
      {label}
    </button>
  );
};

export default InstallAppButton;
